import React from "react";
import { useAgreementFilePath } from "@aspen/hooks";
import { LEGAL_AGREEMENT_FILE_NAME } from "@adgm/libs";

/**
 * 打开 PDF 文档
 * @param agreementFilePath 协议文件路径
 * @param title PDF 文档名称
 */
const openPdf = (agreementFilePath: string, title: string): void => {
  if (!agreementFilePath) {
    console.warn("Agreement file path is not available");
    return;
  }
  const sanitizedTitle = title.replace(/:/g, "");
  const pdfUrl = `${agreementFilePath}${sanitizedTitle}.pdf`;
  window.open(pdfUrl, "_blank", "noopener,noreferrer");
};

/**
 * PDF 文档配置项类型
 */
interface TPdfItem {
  /** PDF 文件名 */
  name: string;
  /** PDF 文档描述 */
  description: string;
}

/**
 * Annexes to the CSA 列表
 */
const ANNEXES_LIST: TPdfItem[] = [
  {
    name: LEGAL_AGREEMENT_FILE_NAME.ANNEX_A_CUSTODY_SERVICES,
    description: "applies where Aspen holds or safeguards digital assets for a client.",
  },
  {
    name: LEGAL_AGREEMENT_FILE_NAME.ANNEX_B_TRADING_SERVICES_MATCHED_PRINCIPAL,
    description:
      "applies where Aspen executes transactions with or for a client on a matched principal basis.",
  },
  {
    name: LEGAL_AGREEMENT_FILE_NAME.ANNEX_C_ADVISORY_AND_ARRANGING_SERVICES,
    description:
      "applies where Aspen provides investment advice to a client or arranges transactions on a client's behalf.",
  },
  {
    name: LEGAL_AGREEMENT_FILE_NAME.ANNEX_D_MANAGING_ASSETS_SERVICES_DISCRETIONARY_MANDATES,
    description: "applies where Aspen manages assets for a client on a discretionary basis.",
  },
];

/**
 * Core Agreement 列表
 */
const CORE_AGREEMENT_LIST: TPdfItem[] = [
  {
    name: LEGAL_AGREEMENT_FILE_NAME.CLIENT_AND_USER_SERVICE_AGREEMENT_CSA,
    description:
      "The principal agreement governing all services provided by Aspen. The CSA applies to all clients and users and incorporates the terms of the annexes. In the event of any inconsistency between the CSA and an annex, the annex shall prevail with respect to the relevant regulated activity.",
  },
];

/**
 * Other Key Documents 列表
 */
const OTHER_DOCUMENTS_LIST: TPdfItem[] = [
  {
    name: LEGAL_AGREEMENT_FILE_NAME.RISK_DISCLOSURE_STATEMENT,
    description:
      "describes the material risks associated with digital assets and with Aspen's services.",
  },
  {
    name: LEGAL_AGREEMENT_FILE_NAME.PRIVACY_POLICY,
    description:
      "governs Aspen's collection, use, storage, and transfer of personal data in compliance with the Abu Dhabi Global Market Data Protection Regulations 2021.",
  },
  {
    name: LEGAL_AGREEMENT_FILE_NAME.YIELD_SERVICES_RISK_DISCLOSURE_STATEMENT,
    description: "describes the risks associated with yield services.",
  },
];

export const PageLegalRegulatory = () => {
  const { agreementFilePath } = useAgreementFilePath();

  /**
   * 渲染 PDF 链接按钮
   * @param pdfName PDF 文件名称（同时用作显示文本）
   */
  const renderPdfButton = (pdfName: string) => {
    return (
      <button
        type="button"
        onClick={() => openPdf(agreementFilePath || "", pdfName)}
        disabled={!agreementFilePath}
        className="text-left cursor-pointer text-blue-600 hover:text-blue-800 decoration-1 hover:decoration-2 font-bold border-0 bg-transparent p-0 m-0 inline transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Open ${pdfName} PDF`}
        title={`Click to view ${pdfName} PDF document`}>
        ● {pdfName} 📄
      </button>
    );
  };

  return (
    <div className="px-[100px] py-[60px] bg-[var(--main-bg-color)] max-md:px-[30px] max-md:py-[30px] max-md:bg-black">
      <div className="text-white text-center text-[28px] font-common-bold mb-5">
        Legal and Regulatory
      </div>
      <div className="text-white text-[14px] font-common-regular">
        <div>
          <div className="text-[18px] font-bold font-common-bold mt-5 mb-5">
            Legal and Regulatory
          </div>
          <div className="indent-[22px]">
            The following documents govern the relationship between Aspen Digital Financial Limited,
            a company incorporated in the Abu Dhabi Global Market with Financial Services Permission
            No. 240034 and regulated by the Financial Services Regulatory Authority
            (&quot;Aspen&quot;), and its clients and users. Together, they set out the terms on
            which Aspen provides its authorised regulated activities, as well as the rights and
            obligations of each client or user.
          </div>
        </div>

        <div>
          <div className="text-[18px] font-bold font-common-bold mt-5 mb-5">Core Agreement</div>
          {CORE_AGREEMENT_LIST.map(agreement => (
            <div key={agreement.name} className="pl-6">
              <strong className="inline-block">{renderPdfButton(agreement.name)}</strong> –{" "}
              {agreement.description}
            </div>
          ))}
        </div>

        <div>
          <div className="text-[18px] font-bold font-common-bold mt-5 mb-5">Annexes to the CSA</div>
          <div className="indent-[22px]">
            Each annex must be read together with the CSA and forms an integral part of the
            client&apos;s agreement with Aspen. The following annexes apply where a client makes use
            of, or is otherwise provided with, the relevant activity. In some cases, such as custody
            of assets, application is implicit and automatic:
          </div>
          {ANNEXES_LIST.map(annex => (
            <div key={annex.name} className="pl-6">
              <strong className="inline-block">{renderPdfButton(annex.name)}</strong> –{" "}
              {annex.description}
            </div>
          ))}
        </div>

        <div>
          <div className="text-[18px] font-bold font-[var(--CommonBold)] mt-5 mb-5">
            Other Key Documents
          </div>
          {OTHER_DOCUMENTS_LIST.map(doc => (
            <div key={doc.name} className="pl-6">
              <strong className="inline-block">{renderPdfButton(doc.name)}</strong> –{" "}
              {doc.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
